import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Drawer, Button, Input, Space, Row, Col, TreeSelect, InputNumber, DatePicker, Modal, Collapse, Empty, Spin, Tag, Radio, Form, Select, Alert, Checkbox, Tooltip } from 'antd';
import { DeleteOutlined, QuestionCircleOutlined, SwapOutlined, UserOutlined } from '@ant-design/icons';
import { isMobile } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import NumberFormat from 'react-number-format';
import BaseRadioList from '@/components/Elements/BaseRadios';
import BaseSelect from '../Elements/BaseSelect';
import { convertToService } from '@/redux/actions/customerServicesActions';
import { fetchServiceDetail } from '@/redux/actions/serviceActions';
import { makeTree, _newLine, customerServiceStatus, paymentMethodOptions, serializeDateTime } from '@/utils/helpers';
import ImageSelector from '../ImageSelector';
import CustomerServiceIndependentStaffFields from './CustomerServiceIndependentStaffFields';
import { buildConversionFinancialSummary, buildServiceCompareData, getServiceConversionEligibility } from './customerServiceConversionPresentation';
import { applyUnknownStaffFieldChange } from './customerServiceFormStateHelpers';

const { Panel } = Collapse;
const { confirm } = Modal;

const moneyFormatter = (value) => `${value ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

const buildServiceItem = (serviceDetail) => ({
    id: serviceDetail.id,
    name: serviceDetail.name,
    regular_price: serviceDetail.price,
    sale_price: serviceDetail.price,
    quantity: 1,
    amount: Number(serviceDetail.price || 0) * 1,
    status: null,
    note: null,
    sales: [],
    sales_unknown: false,
    examination_doctor_ids: [],
    treatment_doctor_ids: [],
    implant_doctor_ids: [],
    prosthetic_doctor_ids: [],
    direct_doctor_unknown: false,
    apprentice_doctor_ids: [],
    assistants: [],
    service_group_id: serviceDetail.service_group_id || serviceDetail.service_group?.id || null,
});

const calcAmount = (item) => Number(item.quantity || 0) * Number(item.sale_price || 0);
const calcTotalAmount = (items) => items.reduce((sum, it) => sum + Number(it.amount || 0), 0);

const ServiceCompareCard = ({ label, service, accentColor, placeholder }) => {
    const data = buildServiceCompareData(service);

    return (
        <div style={{ flex: '1 1 260px', background: '#fff', borderRadius: 6, padding: '8px 10px', border: `1px solid ${accentColor}33` }}>
            <div style={{ color: '#8c8c8c', fontSize: 12, fontWeight: 600 }}>{label}</div>
            <div style={{ color: data.name ? '#262626' : '#8c8c8c', marginTop: 2 }}>
                <span style={{ color: '#8c8c8c' }}>Tên dịch vụ: </span>
                <strong>{data.name || placeholder}</strong>
            </div>
            {data.name && (
                <div style={{ color: '#595959', marginTop: 2 }}>
                    <span style={{ color: '#8c8c8c' }}>Giá bán: </span>
                    <NumberFormat value={data.salePrice} suffix="đ" displayType="text" thousandSeparator />
                    {' × '}<span style={{ color: '#8c8c8c' }}>Số lượng: </span>{data.quantity}
                    {' = '}<strong style={{ color: accentColor }}><NumberFormat value={data.amount} suffix="đ" displayType="text" thousandSeparator /></strong>
                </div>
            )}
        </div>
    );
};

export default function CustomerServiceConvertForm({
    visible,
    onCancel,
    reloadData,
    currentReceipt,
    currentService,
}) {
    const dispatch = useDispatch();
    const branches = useSelector(state => state.config.branches || []);
    const [form] = Form.useForm();

    // ===== selectors =====
    const config = useSelector((state) => state.config);
    const filterData = useSelector((state) => state.filterReducer.list);

    const doctors = config?.doctors || [];
    const assistants = config?.assistants || [];
    const sales = config?.sales || [];
    const implantServiceGroupId = Number(config?.implant_service_group_id) || null;
    const isImplantService = useCallback((item) => (
        implantServiceGroupId !== null && Number(item?.service_group_id) === implantServiceGroupId
    ), [implantServiceGroupId]);

    const servicesTree = useMemo(() => makeTree(filterData?.services || [], 0), [filterData?.services]);

    // ===== local state =====
    const [isLoading, setIsLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);

    const [items, setItems] = useState([]);
    const [uploadedImages, setUploadedImages] = useState([]);

    const [requiredPaymentMethod, setRequiredPaymentMethod] = useState(false);
    const [collectedAmount, setCollectedAmount] = useState(0);
    const [idempotencyKey, setIdempotencyKey] = useState('');

    const totalAmount = useMemo(() => calcTotalAmount(items), [items]);
    const customerBranchId = Number(currentService?.customer?.branch_id) || null;
    const sourceFinancialSnapshot = currentService?.financial_snapshot || {};
    const hasCompletedRefund = Boolean(sourceFinancialSnapshot.has_completed_refund);
    const transferableAmount = Number(sourceFinancialSnapshot.allocated_amount || 0);
    const sourceEligibility = useMemo(() => getServiceConversionEligibility(currentService), [currentService]);
    const financialSummary = useMemo(() => buildConversionFinancialSummary({
        oldServiceAmount: currentService?.amount,
        newServiceAmount: totalAmount,
        allocatedAmount: sourceFinancialSnapshot.allocated_amount,
        outstandingAmount: sourceFinancialSnapshot.outstanding_amount,
        netObligation: sourceFinancialSnapshot.net_obligation,
        customerBalance: sourceFinancialSnapshot.customer_balance,
    }), [currentService, sourceFinancialSnapshot, totalAmount]);

    // ===== sync form values =====
    useEffect(() => {
        if (!visible) return;

        const initialValues = {
            selected_at: moment(),
            payment_at: moment(),
            note: 'Phiếu thu được tạo cho dịch vụ chuyển đổi từ dịch vụ khác.',
            collected_amount: 0,
        };

        if (!idempotencyKey) {
            setIdempotencyKey(`service-conversion-${Date.now()}-${Math.random().toString(36).slice(2)}`);
        }

        // Luôn lấy cơ sở hiện tại của khách; không ghi đè lựa chọn người dùng đã thao tác.
        if (!form.getFieldValue('branch_id') && customerBranchId) {
            initialValues.branch_id = customerBranchId;
        }

        form.setFieldsValue(initialValues);
    }, [visible, form, customerBranchId, idempotencyKey]);

    useEffect(() => {
        if (!visible) return;
        setCollectedAmount((current) => {
            const next = Math.min(Number(current || 0), financialSummary.amountToPay);
            form.setFieldsValue({ collected_amount: next });
            setRequiredPaymentMethod(next > 0);
            return next;
        });
    }, [financialSummary.amountToPay, visible, form]);

    // ===== helpers =====

    const renderNewItems = useCallback(
        (nextItems) => {
            const normalized = (nextItems || []).map((it) => ({ ...it, note: _newLine(it.note) }));
            setItems(normalized);
        },
        []
    );

    // ===== handlers =====
    const handleClose = useCallback(() => {
        onCancel();
        form.resetFields();

        setSaveLoading(false);
        setIsLoading(false);
        setItems([]);
        setUploadedImages([]);

        setRequiredPaymentMethod(false);
        setCollectedAmount(0);
        setIdempotencyKey('');
    }, [onCancel, form]);

    const handleSave = useCallback(async () => {
        try {
            if (!sourceEligibility.canConvert) {
                Modal.error({ content: sourceEligibility.reason });
                return;
            }
            const values = await form.validateFields();

            if (!items.length) {
                Modal.warning({ title: 'Chưa chọn dịch vụ mới', content: 'Vui lòng chọn dịch vụ chuyển đến.' });
                return;
            }
            for (const item of items) {
                if (!item.status) {
                    Modal.warning({ title: 'Thiếu trạng thái', content: `Dịch vụ "${item.name}" chưa chọn trạng thái.` });
                    return;
                }
                if (!(item.sales || []).length && item.sales_unknown !== true) {
                    Modal.warning({ title: 'Thiếu nhân viên sales', content: `Dịch vụ "${item.name}" chưa chọn nhân viên sales hoặc xác nhận Chưa xác định.` });
                    return;
                }
                const directDoctorIds = isImplantService(item)
                    ? [...(item.treatment_doctor_ids || []), ...(item.implant_doctor_ids || []), ...(item.prosthetic_doctor_ids || [])]
                    : (item.treatment_doctor_ids || []);
                if (!directDoctorIds.length && item.direct_doctor_unknown !== true) {
                    Modal.warning({ title: 'Thiếu bác sĩ phụ trách', content: `Dịch vụ "${item.name}" chưa chọn bác sĩ điều trị phù hợp hoặc xác nhận Chưa xác định.` });
                    return;
                }
            }
            setSaveLoading(true);

            // ===== 1) COMMON (chung) =====
            const common = {
                customer_id: currentService?.customer_id || values.customer_id,
                selected_at: serializeDateTime(values.selected_at),
            };

            // ===== 2) SERVICE (chuyển đổi dịch vụ) =====
            const service = {
                service_type: 'CHOT',
                changed_from_service_id: currentService?.service_id,
                customer_service_id: currentService?.id,
                parent_id: currentService?.id,
                from_service_amount: currentService?.amount,

                // data chi tiết dịch vụ mới
                items: items.map((it) => ({
                    id: it.id,
                    name: it.name,
                    quantity: it.quantity,
                    regular_price: it.regular_price,
                    sale_price: it.sale_price,
                    amount: it.amount,
                    status: it.status,
                    note: it.note,

                    // staff
                    sales: it.sales || [],
                    sales_unknown: it.sales_unknown === true,
                    examination_doctor_ids: it.examination_doctor_ids || [],
                    treatment_doctor_ids: it.treatment_doctor_ids || [],
                    implant_doctor_ids: isImplantService(it) ? (it.implant_doctor_ids || []) : [],
                    prosthetic_doctor_ids: isImplantService(it) ? (it.prosthetic_doctor_ids || []) : [],
                    direct_doctor_unknown: it.direct_doctor_unknown === true,
                    apprentice_doctor_ids: it.apprentice_doctor_ids || [],
                    assistants: it.assistants || [],
                })),
            };

            // ===== 3) RECEIPT (phiếu thu / phần tiền) =====
            const receipt = {
                payment_at: serializeDateTime(values.payment_at),
                note: values.note,
                images: uploadedImages,

                // chỉ gửi payment_method khi bắt buộc (giữ logic required_payment_method)
                ...(requiredPaymentMethod ? { payment_method: values.payment_method } : {}),
                ...(requiredPaymentMethod ? {
                    branch_id: values.branch_id,
                    payment_breakdowns: [{ payment_method: values.payment_method, amount: values.collected_amount }],
                } : {}),
                collected_amount: values.collected_amount || 0,
            };

            // ===== FINAL PAYLOAD =====
            const payload = { idempotency_key: idempotencyKey, common, service, receipt };

            await dispatch(convertToService(payload));
            reloadData();
            handleClose();
            setSaveLoading(false);
        } catch (e) {
            setSaveLoading(false);
        }
    }, [form, items, uploadedImages, currentService, requiredPaymentMethod, idempotencyKey, dispatch, reloadData, handleClose, isImplantService, sourceEligibility]);

    const onChangeService = useCallback(
        async (serviceId) => {
            if (!serviceId) return;

            setIsLoading(true);
            try {
                // action cần return detail (promise)
                const detail = await dispatch(fetchServiceDetail(serviceId));

                const newItem = buildServiceItem(detail);

                // giữ nguyên logic: update 1 item (replace)
                const isUpdating = true;
                const merged = isUpdating ? [newItem] : [...items, newItem];
                const unique = merged.filter((v, i, a) => a.findIndex((x) => x.id === v.id) === i);
                const normalized = unique.map((it) => ({ ...it, amount: calcAmount(it) }));

                setItems(normalized);

                setIsLoading(false);
            } catch (err) {
                setIsLoading(false);
            }
        },
        [dispatch, items]
    );

    const onChangeItem = useCallback(
        (name, value, itemId) => {
            const next = [...items];
            const idx = next.findIndex((it) => String(it.id) === String(itemId));
            if (idx < 0) return;

            const current = applyUnknownStaffFieldChange(next[idx], name, value);

            if (name === 'quantity' || name === 'sale_price') {
                current.amount = calcAmount(current);
            }

            if (name === 'amount') {
                const q = Number(current.quantity || 1) || 1;
                current.sale_price = Math.round(Number(current.amount || 0) / q);
            }

            if (name.endsWith('_doctor_ids')) {
                current[name] = Array.from(new Set((value || []).map(Number).filter(Boolean)));
            }

            next[idx] = current;
            renderNewItems(next);
        },
        [items, renderNewItems]
    );

    const onDelete = useCallback(
        (id) => {
            confirm({
                title: 'Cảnh báo',
                content: 'Xóa dịch vụ này?',
                okText: 'OK',
                cancelText: 'Huỷ',
                onOk: () => {
                    const next = items.filter((it) => String(it.id) !== String(id));
                    renderNewItems(next);

                    if (!next.length) {
                        setRequiredPaymentMethod(false);
                        setCollectedAmount(0);
                    }
                },
            });
        },
        [items, renderNewItems]
    );

    const handleImageChange = (fileList, changeDetails) => {
        const images = fileList.map((file) => ({
            manual_id: file.manual_id,
            src: file.originalImage,
        }));
        setUploadedImages(images);
    };

    // ===== UI =====
    return (
        <Drawer
            visible={visible}
            title="Chuyển đổi dịch vụ"
            onClose={handleClose}
            maskClosable={false}
            width={isMobile ? '100%' : '70%'}
            footer={
                <div style={{ textAlign: 'right' }}>
                    <Space>
                        <Button size="large" onClick={handleClose}>
                            Huỷ
                        </Button>
                        <Tooltip title={!sourceEligibility.canConvert ? sourceEligibility.reason : ''}>
                            <span>
                                <Button disabled={!sourceEligibility.canConvert} loading={saveLoading} size="large" type="primary" onClick={handleSave}>
                                    Lưu
                                </Button>
                            </span>
                        </Tooltip>
                    </Space>
                </div>
            }
        >
            {!visible ? null : (
                <Form form={form} layout="vertical" className="customer-data-customer-service-form">
                    {!sourceEligibility.canConvert && (
                        <Alert
                            type="error"
                            message={sourceEligibility.reason}
                            style={{ marginBottom: 12 }}
                        />
                    )}
                    {sourceEligibility.canConvert && sourceEligibility.warning && (
                        <Alert
                            type="warning"
                            message={sourceEligibility.warning}
                            description={(
                                <span>
                                    Đã hoàn <strong>{moneyFormatter(sourceFinancialSnapshot.completed_refund_amount || 0)}đ</strong>;
                                    {' '}số tiền còn phân bổ có thể chuyển là <strong>{moneyFormatter(transferableAmount)}đ</strong>.
                                </span>
                            )}
                            style={{ marginBottom: 12 }}
                        />
                    )}
                    {/* Thông tin khách hàng và quan hệ chuyển đổi */}
                    <div style={{ marginBottom: 12, padding: '10px 12px', background: '#f5f9ff', border: '1px solid #d6e4ff', borderRadius: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <UserOutlined />
                            <span style={{ color: '#595959' }}>Khách hàng</span>
                            <strong className="color-primary">
                                {currentReceipt?.customer?.full_name} - {currentReceipt?.customer?.code}
                            </strong>
                        </div>

                        {sourceEligibility.canConvert && !hasCompletedRefund && (
                            <div style={{ color: '#595959', marginTop: 8 }}>
                                Số tiền còn có thể chuyển:{' '}
                                <strong className="color-primary">
                                    <NumberFormat value={transferableAmount} suffix="đ" displayType="text" thousandSeparator />
                                </strong>
                                {transferableAmount <= 0 && (
                                    <div style={{ color: '#8c8c8c', fontSize: 12, marginTop: 2 }}>
                                        Dịch vụ cũ chưa có tiền thanh toán để chuyển. Số cần thu được tính từ giá dịch vụ mới, sau khi trừ số dư nếu có.
                                    </div>
                                )}
                            </div>
                        )}

                        <div style={{ display: 'flex', alignItems: 'stretch', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                            <ServiceCompareCard
                                label="DỊCH VỤ CŨ"
                                service={currentService}
                                accentColor="#597ef7"
                                placeholder="Chưa có thông tin dịch vụ cũ"
                            />
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1890ff', padding: '0 4px' }}>
                                <SwapOutlined style={{ fontSize: 18 }} />
                            </div>
                            <ServiceCompareCard
                                label="DỊCH VỤ MỚI"
                                service={items[0] ? { ...items[0], amount: totalAmount } : {}}
                                accentColor="#1890ff"
                                placeholder="Chọn dịch vụ chuyển đến bên dưới"
                            />
                        </div>
                    </div>

                    {/* Select service */}
                    <div style={{ background: '#fff', border: '1px solid #f0f0f0', borderRadius: 8, padding: 12 }}>
                        <div style={{ fontWeight: 700, marginBottom: 8 }}>1. Thông tin dịch vụ mới</div>
                        <Row gutter={12}>
                            <Col xs={24} md={14}>
                                <Form.Item name="selected_service_id" label="Dịch vụ chuyển đến" tooltip="Chọn dịch vụ sẽ tiếp nhận nghĩa vụ và lịch sử liên quan của dịch vụ hiện tại; hệ thống tự tính phần chênh lệch cần thu thêm hoặc chuyển về số dư khách.">
                                    <TreeSelect
                                        showSearch
                                        style={{ width: '100%' }}
                                        dropdownStyle={{ maxHeight: 450, overflow: 'auto' }}
                                        treeData={servicesTree || []}
                                        treeNodeFilterProp="title"
                                        allowClear
                                        placeholder="Chọn một dịch vụ"
                                        treeDefaultExpandAll
                                        onChange={onChangeService}
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={10}>
                                <Form.Item
                                    name="selected_at"
                                    label="Ngày chốt"
                                    tooltip="Ngày dịch vụ chuyển đổi bắt đầu có hiệu lực, dùng để xác định nghĩa vụ thanh toán và các điều kiện nghiệp vụ phát sinh sau chuyển đổi."
                                    rules={[{ required: true, message: 'Vui lòng điền!' }]}
                                >
                                    <DatePicker className="w-100" format="DD/MM/YYYY" />
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>

                    <div style={{ height: 12 }} />

                    {/* Selected items */}
                    <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8, padding: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <div style={{ fontWeight: 700 }}>2. Chi tiết dịch vụ và nhân sự</div>
                            {!!items.length && (
                                <Tag color="purple">
                                    Tổng: <NumberFormat value={totalAmount} suffix="đ" displayType="text" thousandSeparator />
                                </Tag>
                            )}
                        </div>

                        <Spin tip="Loading..." spinning={isLoading}>
                            <Collapse expandIconPosition="end" ghost defaultActiveKey={['0']}>
                                {items.length > 0 ? (
                                    items.map((item, index) => (
                                        <Panel
                                            key={0}
                                            collapsible="disabled"
                                            header={<span className="chingo-normal-title">{index + 1}. {item.name}</span>}
                                        >
                                            <Row gutter={12}>
                                                <Col xs={24} md={8}>
                                                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Số lượng</div>
                                                    <InputNumber
                                                        formatter={moneyFormatter}
                                                        value={item.quantity}
                                                        min={1}
                                                        className="w-100"
                                                        onChange={(val) => {
                                                            const valid = val == null || val <= 0 ? 1 : val;
                                                            onChangeItem('quantity', valid, item.id);
                                                        }}
                                                    />
                                                </Col>

                                                <Col xs={24} md={8}>
                                                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Giá bán</div>
                                                    <InputNumber
                                                        formatter={moneyFormatter}
                                                        value={item.sale_price}
                                                        min={0}
                                                        className="w-100"
                                                        onChange={(val) => onChangeItem('sale_price', val, item.id)}
                                                    />
                                                </Col>

                                                <Col xs={24} md={8}>
                                                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Thành tiền</div>
                                                    <InputNumber
                                                        formatter={moneyFormatter}
                                                        value={item.amount}
                                                        min={0}
                                                        className="w-100"
                                                        onChange={(val) => onChangeItem('amount', val, item.id)}
                                                    />
                                                </Col>

                                                <Col xs={24} md={24} className="mt-2">
                                                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Trạng thái</div>
                                                    <Radio.Group value={item.status} onChange={(e) => onChangeItem('status', e.target.value, item.id)}>
                                                        {customerServiceStatus?.map((opt) => (
                                                            <Radio key={opt.value} value={opt.value}>
                                                                {opt.label}
                                                            </Radio>
                                                        ))}
                                                    </Radio.Group>
                                                </Col>

                                                <Col xs={24} md={24} className="mt-2">
                                                    <Row gutter={12}>
                                                        <Col xs={24} md={12}>
                                                            <div style={{ fontWeight: 600, marginBottom: 6 }}>Nhân viên sales</div>
                                                            <BaseSelect
                                                                options={sales}
                                                                optionValue="user_id"
                                                                optionLabel="display_name"
                                                                mode="multiple"
                                                                showSearch
                                                                style={{ width: '100%' }}
                                                                value={item.sales || []}
                                                                disabled={item.sales_unknown === true}
                                                                placeholder="Chọn 1 hoặc nhiều"
                                                                onChange={(vals) => onChangeItem('sales', vals, item.id)}
                                                            />
                                                            <Checkbox
                                                                checked={item.sales_unknown === true}
                                                                onChange={(event) => onChangeItem('sales_unknown', event.target.checked, item.id)}
                                                                style={{ marginTop: 6 }}
                                                            >
                                                                Chưa xác định
                                                            </Checkbox>
                                                        </Col>

                                                        <Col xs={24} md={12}>
                                                            <div style={{ fontWeight: 600, marginBottom: 6 }}>Phụ tá</div>
                                                            <BaseSelect
                                                                options={assistants}
                                                                optionValue="user_id"
                                                                optionLabel="display_name"
                                                                mode="multiple"
                                                                showSearch
                                                                style={{ width: '100%' }}
                                                                value={item.assistants || []}
                                                                placeholder="Chọn 1 hoặc nhiều"
                                                                onChange={(vals) => onChangeItem('assistants', vals, item.id)}
                                                            />
                                                        </Col>
                                                    </Row>
                                                    <Row gutter={12} style={{ marginTop: 8 }}>
                                                        <CustomerServiceIndependentStaffFields
                                                            item={item}
                                                            doctors={doctors}
                                                            isImplant={isImplantService(item)}
                                                            onChangeItem={onChangeItem}
                                                            formItemStyle={{ marginBottom: 0 }}
                                                        />
                                                    </Row>
                                                </Col>

                                                <Col xs={24} md={24} className="mt-2">
                                                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Ghi chú</div>
                                                    <Input.TextArea value={item.note} rows={3} onChange={(e) => onChangeItem('note', e.target.value, item.id)} />
                                                </Col>

                                                <Col xs={24} md={24} className="mt-2" style={{ textAlign: 'right' }}>
                                                    <Button onClick={() => onDelete(item.id)} danger icon={<DeleteOutlined />} size="small">
                                                        Xóa
                                                    </Button>
                                                </Col>
                                            </Row>
                                        </Panel>
                                    ))
                                ) : (
                                    <div style={{ textAlign: 'center' }}>
                                        <Empty imageStyle={{ height: 30 }} style={{ margin: '10px 0' }} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                                    </div>
                                )}
                            </Collapse>
                        </Spin>
                    </div>

                    <div style={{ marginTop: 20, background: '#fff', border: '1px solid #f0f0f0', borderRadius: 8, padding: 12 }}>
                        <div style={{
                            margin: '10px 0 12px',
                            padding: '10px 12px',
                            borderRadius: 6,
                            border: `1px solid ${financialSummary.action === 'COLLECT' ? '#ffa39e' : financialSummary.action === 'RETURN_TO_BALANCE' ? '#b7eb8f' : '#d9d9d9'}`,
                            background: financialSummary.action === 'COLLECT' ? '#fff1f0' : financialSummary.action === 'RETURN_TO_BALANCE' ? '#f6ffed' : '#fafafa',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 8,
                            flexWrap: 'wrap',
                        }}>
                            <div>
                                <div style={{ color: '#595959', fontSize: 12, display: 'flex', alignItems: 'center', gap: 5 }}>
                                    <span>KẾT QUẢ CẦN XỬ LÝ</span>
                                    <Tooltip
                                        placement="topLeft"
                                        overlayStyle={{ maxWidth: 420 }}
                                        title={(
                                            <div style={{ lineHeight: 1.5 }}>
                                                <div><strong>Tổng doanh số mới</strong> = Tổng doanh số cũ - Giá dịch vụ cũ + Giá dịch vụ mới.</div>
                                                <div><strong>Tổng đã thanh toán mới</strong> = Tổng đã thanh toán cũ + Tiền thực tế thu thêm.</div>
                                                <div><strong>Công nợ mới</strong> = Công nợ cũ - Công nợ còn lại của dịch vụ cũ + Công nợ dịch vụ mới.</div>
                                                <div><strong>Số cần thu</strong> = Giá dịch vụ mới - Tiền được chuyển - Số dư sử dụng.</div>
                                            </div>
                                        )}
                                    >
                                        <QuestionCircleOutlined tabIndex={0} aria-label="Xem cách tính sau chuyển đổi" style={{ color: '#8c8c8c', cursor: 'help' }} />
                                    </Tooltip>
                                </div>
                                <strong>
                                    {financialSummary.action === 'COLLECT' && 'Cần thu thêm'}
                                    {financialSummary.action === 'RETURN_TO_BALANCE' && 'Chuyển về số dư khách hàng'}
                                    {financialSummary.action === 'NONE' && 'Không phát sinh xử lý tài chính'}
                                </strong>
                                <div style={{ color: '#8c8c8c', fontSize: 11, lineHeight: 1.35, marginTop: 3 }}>
                                    Chỉ chuyển số tiền khách đã thanh toán cho dịch vụ cũ; không lấy giá dịch vụ cũ trừ giá dịch vụ mới.
                                    <br />
                                    Công thức: Giá dịch vụ mới - tiền được chuyển - số dư sử dụng = số cần thu.
                                </div>
                                {financialSummary.action === 'COLLECT' && (
                                    <div style={{ color: '#595959', fontSize: 12, lineHeight: 1.4, marginTop: 5 }}>
                                        Giá dịch vụ mới {moneyFormatter(financialSummary.newAmount)}đ
                                        {' - '}tiền được chuyển {moneyFormatter(financialSummary.sourceAppliedAmount)}đ
                                        {financialSummary.existingBalanceAppliedAmount > 0 && (
                                            <> - số dư sử dụng {moneyFormatter(financialSummary.existingBalanceAppliedAmount)}đ</>
                                        )}
                                        {' = '}<strong>cần thu thêm {moneyFormatter(financialSummary.amountToPay)}đ</strong>.
                                    </div>
                                )}
                            </div>
                            {financialSummary.action !== 'NONE' && (
                                <div style={{ fontSize: 20, fontWeight: 700, color: financialSummary.action === 'COLLECT' ? '#cf1322' : '#389e0d' }}>
                                    <NumberFormat
                                        value={financialSummary.action === 'COLLECT' ? financialSummary.amountToPay : financialSummary.amountToReturnToBalance}
                                        suffix="đ"
                                        displayType="text"
                                        thousandSeparator
                                    />
                                </div>
                            )}
                        </div>

                        {financialSummary.amountToPay > 0 && (
                            <div style={{ padding: '10px 12px', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6, marginBottom: 12 }}>
                                <Form.Item
                                    name="collected_amount"
                                    label="Số tiền khách thực trả tại thời điểm chuyển đổi"
                                    tooltip="Nhập 0 nếu khách chưa thanh toán. Hệ thống chỉ tạo phiếu thu cho đúng số tiền thực nhận; phần còn lại tiếp tục là công nợ dịch vụ mới."
                                    rules={[
                                        { required: true, message: 'Vui lòng nhập số tiền thực thu, có thể bằng 0.' },
                                        {
                                            validator: (_, value) => Number(value || 0) <= financialSummary.amountToPay
                                                ? Promise.resolve()
                                                : Promise.reject(new Error('Số tiền thực thu không được vượt công nợ sau chuyển đổi.')),
                                        },
                                    ]}
                                    style={{ marginBottom: 0 }}
                                >
                                    <InputNumber
                                        min={0}
                                        max={financialSummary.amountToPay}
                                        formatter={moneyFormatter}
                                        className="w-100"
                                        onChange={(value) => {
                                            const next = Math.max(0, Number(value || 0));
                                            setCollectedAmount(next);
                                            setRequiredPaymentMethod(next > 0);
                                        }}
                                    />
                                </Form.Item>
                                <div style={{ color: '#8c8c8c', fontSize: 11, marginTop: 5 }}>
                                    Công nợ còn lại sau lần thu này:{' '}
                                    <strong>
                                        <NumberFormat value={Math.max(0, financialSummary.amountToPay - collectedAmount)} suffix="đ" displayType="text" thousandSeparator />
                                    </strong>
                                </div>
                            </div>
                        )}

                        {requiredPaymentMethod && (
                            <div style={{ padding: '10px 12px', background: '#fafafa', borderRadius: 6, marginBottom: 12 }}>
                                <div style={{ fontWeight: 600, marginBottom: 8 }}>Thông tin thu phần chênh lệch</div>
                                <Row gutter={12}>
                                    <Col xs={24} md={12}>
                                        <Form.Item name="payment_method" label="Hình thức thu" tooltip="Chọn cách khách thanh toán phần chênh lệch để ghi nhận đúng giao dịch và phục vụ đối chiếu." rules={[{ required: true, message: 'Vui lòng chọn hình thức thu phần chênh lệch.' }]}>
                                            <BaseRadioList options={paymentMethodOptions ?? []} showSearch placeholder="Chọn hình thức thu" className="w-100" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={12}>
                                        <Form.Item name="branch_id" label="Cơ sở thực tế thu tiền"
                                            rules={[{ required: true, message: 'Vui lòng chọn cơ sở thực tế thu tiền.' }]}>
                                            <Select showSearch optionFilterProp="label" options={branches.map(branch => ({ value: branch.id, label: branch.name }))} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={12}>
                                        <Form.Item name="payment_at" label="Ngày thu" tooltip="Ngày thực tế nhận phần tiền chênh lệch, dùng làm thời điểm nguồn cho phân bổ và tính hoa hồng." rules={[{ required: true, message: 'Vui lòng chọn ngày thực tế nhận tiền.' }]} style={{ marginBottom: 0 }}>
                                            <DatePicker className="w-100" format="DD/MM/YYYY" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </div>
                        )}

                        <Form.Item name="note" label="Lý do / Ghi chú chuyển đổi" tooltip="Ghi căn cứ chuyển đổi hoặc thông tin cần lưu lại để người kiểm tra hiểu giao dịch này.">
                            <Input.TextArea rows={3} />
                        </Form.Item>

                        {requiredPaymentMethod && <Form.Item name="images" label="Ảnh phiếu" tooltip="Đính kèm ảnh chứng từ thu phần chênh lệch nếu có để phục vụ kiểm tra và đối chiếu." className="flex-upload">
                            <ImageSelector
                                onChangeData={(fileList, changeDetails) => handleImageChange(fileList, changeDetails)} // Gắn hàm xử lý thay đổi dữ liệu
                                multiple={true} // Cho phép chọn nhiều ảnh
                                maxCount={50} // Giới hạn tối đa 50 ảnh
                                uploadText="Chọn ảnh"
                                defaultFileList={[]}
                            />
                        </Form.Item>}
                    </div>
                </Form>
            )}
        </Drawer>
    );
}
